import axios from 'axios'
import i18n from '@/plugins/i18n';

// Dynamically determine API base URL based on current location
const baseURL = window.location.origin + '/api';

const AXIOS = axios.create({
    baseURL: baseURL,
    timeout: 10000
});

// Add locale as a request interceptor so it uses the current locale dynamically
AXIOS.interceptors.request.use(config => {
    config.params = config.params || {};
    config.params.locale = i18n.global.locale.value;
    return config;
});

export default {

    register(email, password) {
        return AXIOS.get(`/registration/` + email + '/' + password);
    },

    registrationConfirm(code) {
        return AXIOS.post(`/registrationConfirm/` + code);
    },

    login(email, password, deviceId) {
        return AXIOS.get(`/login/` + email + '/' + password + '/' + deviceId);
    },

    sendPasswordUpdateEmail(email) {
        return AXIOS.get(`/sendPasswordUpdateEmail/` + email);
    },

    updatePassword(code, password) {
        return AXIOS.put(`/updatePassword/` + code + '/' + password);
    },

    checkTokenExist(token) {
        return AXIOS.get(`/checkTokenExist/` + token);
    },

    deleteTokenByDeviceId(deviceId) {
        AXIOS.post(`/deleteTokenByDeviceId/` + deviceId);
    },

    getBlockedDates(startDate, endDate) {
        return AXIOS.get(`/reservations/blocked-dates`, {
            params: { start_date: startDate, end_date: endDate }
        });
    },

    getTotalPrice(startDate, endDate) {
        return AXIOS.get(`/reservations/total-price`, {
            params: { start_date: startDate, end_date: endDate }
        });
    },

    getImageSortOrder(gallery = 'hillwood', propertyId = null) {
        const params = { gallery };
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.get(`/gallery/sort-order`, { params });
    },

    saveImageSortOrder(gallery, filenames, propertyId = null) {
        const body = { gallery, filenames };
        if (propertyId) body.propertyId = propertyId;
        return AXIOS.put(`/gallery/sort-order`, body);
    },

    // Fees CRUD
    getFees(propertyId = null) {
        const params = {};
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.get(`/fees`, { params });
    },

    getFee(id) {
        return AXIOS.get(`/fees/${id}`);
    },

    createFee(fee, propertyId = null) {
        const params = {};
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.post(`/fees`, fee, { params });
    },

    updateFee(id, fee, propertyId = null) {
        const params = {};
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.put(`/fees/${id}`, fee, { params });
    },

    deleteFee(id) {
        return AXIOS.delete(`/fees/${id}`);
    },

    // Reservations / Bookings CRUD
    getReservations(propertyId = null) {
        const params = {};
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.get(`/bookings`, { params });
    },

    getReservation(id) {
        return AXIOS.get(`/bookings/${id}`);
    },

    createReservation(reservation) {
        return AXIOS.post(`/bookings`, reservation);
    },

    updateReservation(id, reservation) {
        return AXIOS.put(`/bookings/${id}`, reservation);
    },

    deleteReservation(id) {
        return AXIOS.delete(`/bookings/${id}`);
    },

    // PayPal
    getPayPalClientId() {
        return AXIOS.get(`/paypal/client-id`);
    },

    createPayPalOrder(amount, description, payerEmail) {
        return AXIOS.post(`/paypal/orders`, { amount, description, payerEmail });
    },

    capturePayPalOrder(orderId, bookingDetails) {
        return AXIOS.post(`/paypal/orders/${orderId}/capture`, bookingDetails || {});
    },

    // Profile
    getProfile(token) {
        return AXIOS.get(`/profile`, { params: { token } });
    },

    updateProfile(token, profileData) {
        return AXIOS.put(`/profile`, profileData, { params: { token } });
    },

    // Configurations
    getConfigurations() {
        return AXIOS.get(`/config`);
    },

    getMinimumBookingDays() {
        return AXIOS.get(`/config/minimum-booking-days`);
    },

    getShowPriceBreakdowns() {
        return AXIOS.get(`/config/show-price-breakdowns`);
    },

    // Properties CRUD
    getProperties() {
        return AXIOS.get(`/properties`);
    },

    getProperty(id) {
        return AXIOS.get(`/properties/${id}`);
    },

    getPropertyBySlug(slug) {
        return AXIOS.get(`/properties/slug/${slug}`);
    },

    createProperty(property) {
        return AXIOS.post(`/properties`, property);
    },

    updateProperty(id, property) {
        return AXIOS.put(`/properties/${id}`, property);
    },

    deleteProperty(id) {
        return AXIOS.delete(`/properties/${id}`);
    },

    // Discounts CRUD
    getDiscounts(propertyId = null) {
        const params = {};
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.get(`/discounts`, { params });
    },

    getActiveDiscounts(propertyId = null) {
        const params = {};
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.get(`/discounts/active`, { params });
    },

    getDiscount(id) {
        return AXIOS.get(`/discounts/${id}`);
    },

    createDiscount(discount, propertyId = null) {
        const params = {};
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.post(`/discounts`, discount, { params });
    },

    updateDiscount(id, discount, propertyId = null) {
        const params = {};
        if (propertyId) params.propertyId = propertyId;
        return AXIOS.put(`/discounts/${id}`, discount, { params });
    },

    deleteDiscount(id) {
        return AXIOS.delete(`/discounts/${id}`);
    },

    // Visitor tracking
    recordVisit(page) {
        return AXIOS.post(`/visitors/record`, { page });
    },

    getVisitorSummary(from, to) {
        return AXIOS.get(`/visitors/summary`, { params: { from, to } });
    },

    getVisitorLogs(from, to) {
        return AXIOS.get(`/visitors`, { params: { from, to } });
    },

    // Inquiries
    getInquiries() {
        return AXIOS.get(`/inquiries`);
    },

    deleteInquiry(id) {
        return AXIOS.delete(`/inquiries/${id}`);
    },

    // Hospitable reservations
    getHospitableReservations(startDate, endDate) {
        return AXIOS.get(`/hospitable/reservations`, {
            params: { start_date: startDate, end_date: endDate }
        });
    }
}


